import { IntlShape } from 'react-intl';
import { z } from 'zod';
import {
  Credential,
  CredentialSecret,
  credentialTypes,
  isKnownCredentialType,
} from './types';

const SCOPE_SEPARATOR = ' ';

export const credentialFormSchema = (intl: IntlShape) => {
  const required = intl.formatMessage({
    id: 'features.credentials.form.error.required',
  });

  return z.object({
    name: z.string().trim().min(1, required),
    type: z
      .string()
      .refine((value): boolean => isKnownCredentialType(value), required),
    baseApiUrl: z.string().trim().min(1, required),
    clientId: z.string().trim().min(1, required),
    clientSecret: z.string().trim().min(1, required),
    permissionScopes: z.array(z.string()),
  });
};

export type CredentialFormValues = z.infer<
  ReturnType<typeof credentialFormSchema>
>;

/**
 * The API never returns a stored client secret, it returns a `[redacted...]`
 * placeholder. Sending that placeholder back is a no-op on the secret, so the
 * form can round-trip it like any other value.
 */
export const toCredentialFormValues = (
  credential?: Credential
): CredentialFormValues =>
  credential
    ? {
        name: credential.name,
        type: credential.type,
        baseApiUrl: credential.secret.baseApiUrl,
        clientId: credential.secret.clientId,
        clientSecret: credential.secret.clientSecret,
        permissionScopes: (credential.secret.scope ?? '')
          .split(SCOPE_SEPARATOR)
          .filter(Boolean),
      }
    : {
        name: '',
        type: credentialTypes[0],
        baseApiUrl: '',
        clientId: '',
        clientSecret: '',
        permissionScopes: [],
      };

/**
 * Scopes are optional, but the key is always sent - an empty string clears
 * stored scopes, while a missing key would leave them unchanged on PATCH.
 */
export const toCredentialSecret = (
  values: CredentialFormValues
): CredentialSecret => ({
  clientId: values.clientId,
  clientSecret: values.clientSecret,
  baseApiUrl: values.baseApiUrl,
  scope: values.permissionScopes.join(SCOPE_SEPARATOR),
});
